import React, { useEffect, useCallback, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import styles from "./userManagement.module.scss";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import TablePagination from "../../components/tablePagination/TablePagination";
import { selectUser, setToast, selectToast } from "../../app/appSlice";
import {
  fetchUsers,
  searchUsers,
  setSearchText,
  setSorting,
  updateCurrentPage,
  updateItemsPerPage,
  clearError,
  selectUsers,
  selectUserManagementLoading,
  selectUserManagementError,
  selectPagination,
  selectSorting,
  selectSearchText,
  selectUserStats
} from "./userManagementSlice";
import constants from "../../utilities/constants";
import { FaSort, FaEye, FaInfoCircle } from "react-icons/fa";
import UserManagementStats from "./components/UserManagementStats";

const UserManagement = () => {
  const dispatch = useDispatch();
  const searchTimeoutId = useRef(null);
  const [searchDisabled, setSearchDisabled] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  // Redux selectors
  const currentUser = useSelector(selectUser);
  const toast = useSelector(selectToast); // get toast state
  const users = useSelector(selectUsers);
  const isLoading = useSelector(selectUserManagementLoading);
  const error = useSelector(selectUserManagementError);
  const pagination = useSelector(selectPagination);
  const sorting = useSelector(selectSorting);
  const searchText = useSelector(selectSearchText);
  const stats = useSelector(selectUserStats);

  // Helper function to parse role from JSON string array
  const parseUserRole = (roleString) => {
    try {
      if (typeof roleString === 'string') {
        const parsed = JSON.parse(roleString);
        return Array.isArray(parsed) ? parsed[0] : roleString;
      }
      return roleString || "Default Access";
    } catch (error) {
      return roleString || "Default Access";
    }
  };

  // Check if user has default role
  const isDefaultUser = (roleString) => {
    const role = parseUserRole(roleString);
    return role === "Default Access" || role === "default" || role === "Default";
  };

  // Check if current user is admin
  const isAdmin = currentUser?.roles?.includes(constants.userRoles.admin);

  // Initial load
  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchUsers());
    }
  }, [isAdmin, dispatch]);

  // Clear errors after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Disable search input if "No result found" toast is open
  useEffect(() => {
    setSearchDisabled(!!toast?.isOpen && toast?.message === "No result found!");
  }, [toast]);

  // Search functionality with debouncing
  const handleSearch = useCallback((event) => {
    const searchValue = event.target.value;
    dispatch(setSearchText(searchValue));

    if (searchTimeoutId.current) {
      clearTimeout(searchTimeoutId.current);
    }

    searchTimeoutId.current = setTimeout(async () => {
      const result = await dispatch(searchUsers({ 
        searchText: searchValue, 
        filters: {} 
      }));
      // Show toast if no users found and search is not empty
      if (searchValue && result.payload && result.payload.length === 0) {
        dispatch(
          setToast({
            isOpen: true,
            message: "No result found!",
            onDismiss: () => dispatch(setToast({ isOpen: false })),
          })
        );
      }
    }, 500);
  }, [dispatch]);

  // Sorting functionality
  const handleSort = (field) => {
    dispatch(setSorting({ field }));
    // Trigger page update to apply new sorting
    dispatch(updateCurrentPage({ currentPage: pagination.currentPage }));
  };

  // Pagination handlers
  const onPageChange = ({ gotoPage }) => {
    dispatch(updateCurrentPage({ currentPage: gotoPage }));
  };

  const onItemsPerPageChange = (limit) => {
    dispatch(updateItemsPerPage({ itemsPerPage: limit }));
  };
  // View user handlers
  const handleViewUser = (user) => {
    setViewUser(user);
  };

  const handleCloseViewModal = () => {
    setViewUser(null);
  };

  // Render access denied if not admin
  if (!isAdmin) {
    return (
      <div className={styles.body}>
        <Helmet>
          <title>Shared Data Portal | Access Denied</title>
        </Helmet>
        <div className={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page. Admin role required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <Helmet>
        <title>Shared Data Portal | User Management</title>
      </Helmet>
      
      <div className={styles.pageNameContainer}>
        <div className={styles.pageName}>User Management</div>
        <Breadcrumbs />
      </div>

      {/* User Statistics */}
      <UserManagementStats stats={stats} />

      {/* Error Display */}
      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button onClick={() => dispatch(clearError())} className={styles.closeError}>×</button>
        </div>
      )}

      <div className={styles.list}>
        <div className={styles.data_filter}>
          <div className={styles.inputIcons}>
            <form className={styles.search}>
              <input
                onChange={handleSearch}
                value={searchText || ''}
                className={styles.search}
                type="search"
                placeholder="Search name, email, or role"
                disabled={isLoading || searchDisabled}
              />
            </form>
          </div>
        </div>

        <div className={styles.table}>
          <div className={styles.tableContainer}>
            <div style={{ width: "100%" }}>
              <div className={styles.header}>
                <div onClick={() => handleSort("Name")}>
                  <span>Name</span>
                  <FaSort className={styles.icon} />
                  {sorting.field === 'Name' && (
                    <span className={styles.sortIndicator}>
                      {sorting.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                <div onClick={() => handleSort("Email")}>
                  <span>Email</span>
                  <FaSort className={styles.icon} />
                  {sorting.field === 'Email' && (
                    <span className={styles.sortIndicator}>
                      {sorting.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                <div onClick={() => handleSort("Role")}>
                  <span>Role</span>
                  <FaSort className={styles.icon} />
                  {sorting.field === 'Role' && (
                    <span className={styles.sortIndicator}>
                      {sorting.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                <div onClick={() => handleSort("createdAt")}>
                  <span>Created At</span>
                  <FaSort className={styles.icon} />
                  {sorting.field === 'createdAt' && (
                    <span className={styles.sortIndicator}>
                      {sorting.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                <div style={{ maxWidth: "0.8rem" }}>Actions</div>
              </div>

              {isLoading ? (
                <div className={styles.loading}>
                  <div className={styles.loadingContent}>
                    <div className={styles.spinner}></div>
                    <span>Loading users...</span>
                  </div>
                </div>
              ) : users.length === 0 ? (
                <div className={styles.noData}>
                  <p>No users found</p>
                  {searchText && (
                    <small>Try adjusting your search criteria</small>
                  )}
                </div>
              ) : (
                users.map((userItem) => (
                  <div key={userItem.Email} className={styles.contentRow}>
                    <div className={styles.td}>{userItem.Name}</div>
                    <div className={styles.td}>{userItem.Email}</div>
                    <div className={styles.td}>
                      <div className={`${styles.role} ${styles[parseUserRole(userItem.Role).toLowerCase().replace(/\s+/g, '')]}`}>
                        {parseUserRole(userItem.Role)}
                      </div>
                    </div>
                    <div className={styles.td}>
                      {userItem.createdAt ? new Date(userItem.createdAt).toLocaleDateString() : '-'}
                    </div>
                     <div className={styles.td} style={{ maxWidth: "0.8rem" }}>
                      <FaEye
                        className={styles.viewIcon}
                        title="View User Details"
                        onClick={() => handleViewUser(userItem)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <TablePagination
          total={pagination.totalItems}
          currentPage={pagination.currentPage}
          onPageChange={onPageChange}
          itemsPerPage={pagination.itemsPerPage}
          items={users}
          onItemsPerPageChange={onItemsPerPageChange}
          showTotal={true}
        />
      </div>
      {/* View User Modal */}
      {viewUser && (
        <div className={styles.modalOverlay} onClick={handleCloseViewModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>User Details</h3>
              <button 
                className={styles.closeButton} 
                onClick={handleCloseViewModal}
              >
                ×
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.formGroup}>
                <label>Name</label>
                 <input 
                  type="text"
                  className={styles.readonlyInput}
                  value={viewUser.Name || ''}
                  readOnly
                  disabled
                />              </div>
              <div className={styles.formGroup}>
                <label>Email</label>
                <input 
                  type="email"
                  className={styles.readonlyInput}
                  value={viewUser.Email || ''}
                  readOnly
                  disabled
                /></div>
              <div className={styles.formGroup}>
                <label>Role</label>
                <input 
                  type="text"
                  className={styles.readonlyInput}
                  value={parseUserRole(viewUser.Role)}
                  readOnly
                  disabled
                />
              </div>
                <div className={styles.roleChangeNote}>
                  <FaInfoCircle className={styles.infoIcon} />
                  <div className={styles.noteContent}>
                    <strong>Note:</strong> To change this user's role, please contact the 
                    application owner. Role changes must be made through Azure AD by an authorized administrator.
                  </div>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
